import { supabase } from '../lib/supabase';

export const prospectService = {
  /**
   * Create prospect and send quote email
   * @param {Object} prospectData - Prospect information
   * @param {string} prospectData.name - Prospect name
   * @param {string} prospectData.email - Prospect email
   * @param {string} prospectData.phone - Prospect phone (optional)
   * @param {Array} prospectData.quoteItems - Selected quote items
   * @param {number} prospectData.totalAmount - Total quote amount
   * @returns {Promise<Object>} Created prospect and email status
   */
  async createProspectAndSendEmail(prospectData) {
    try {
      const { name, email, phone, quoteItems, totalAmount } = prospectData;

      // Validate required fields
      if (!name || !email || !quoteItems || !totalAmount) {
        throw new Error('Missing required fields');
      }

      // Insert prospect into database
      const { data: prospect, error: dbError } = await supabase
        ?.from('prospects')
        ?.insert({
          name,
          email,
          phone: phone || null,
          quote_items: quoteItems,
          total_amount: totalAmount
        })
        ?.select()
        ?.single();

      if (dbError) throw dbError;

      // Send email via edge function
      const { data: emailResult, error: emailError } = await supabase?.functions?.invoke(
        'send-quote-email',
        {
          body: {
            name,
            email,
            quoteItems,
            totalAmount
          }
        }
      );

      if (emailError) {
        console.error('Email sending failed:', emailError);
        // Don't throw - prospect is saved, email failure is non-critical
      }

      return {
        success: true,
        prospect: {
          id: prospect?.id,
          name: prospect?.name,
          email: prospect?.email,
          phone: prospect?.phone,
          quoteItems: prospect?.quote_items,
          totalAmount: parseFloat(prospect?.total_amount),
          createdAt: prospect?.created_at
        },
        emailSent: !emailError,
        emailResult
      };
    } catch (error) {
      console.error('Error creating prospect:', error);
      throw error;
    }
  },

  /**
   * Create professional prospect with AI-generated quote and send confirmation email
   * @param {Object} prospectData - Professional prospect information
   * @returns {Promise<Object>} Created prospect and email status
   */
  async createProfessionalProspectWithQuote(prospectData) {
    try {
      const { 
        companyName, 
        contactName, 
        email, 
        phone, 
        clientType, 
        shippingType, 
        needsEX1,
        pickupAddress,
        frequency,
        message,
        quoteContent,
        estimatedAmount,
        professionalClientId
      } = prospectData;

      // Validate required fields
      if (!contactName || !email || !quoteContent) {
        throw new Error('Missing required fields: contactName, email, quoteContent');
      }

      // Calculate reminder date (7 days from now)
      const reminderDate = new Date();
      reminderDate?.setDate(reminderDate?.getDate() + 7);

      // Insert professional prospect into database
      const { data: prospect, error: dbError } = await supabase
        ?.from('prospects')
        ?.insert({
          name: contactName,
          email,
          phone: phone || null,
          quote_items: [{
            companyName,
            clientType,
            shippingType,
            needsEX1,
            pickupAddress,
            frequency,
            message
          }],
          total_amount: estimatedAmount || 0,
          quote_content: quoteContent,
          quote_status: 'pending',
          reminder_scheduled_for: reminderDate?.toISOString(),
          professional_client_id: professionalClientId || null
        })
        ?.select()
        ?.single();

      if (dbError) throw dbError;

      // Send confirmation email via edge function
      const { data: emailResult, error: emailError } = await supabase?.functions?.invoke(
        'send-professional-quote-confirmation',
        {
          body: {
            prospectId: prospect?.id,
            companyName,
            contactName,
            email,
            quoteContent,
            clientType,
            shippingType
          }
        }
      );

      // Update confirmation email timestamp if sent successfully
      if (!emailError) {
        await supabase
          ?.from('prospects')
          ?.update({ confirmation_email_sent_at: new Date()?.toISOString() })
          ?.eq('id', prospect?.id);
      } else {
        console.error('Confirmation email sending failed:', emailError);
      }

      return {
        success: true,
        prospect: {
          id: prospect?.id,
          name: prospect?.name,
          email: prospect?.email,
          phone: prospect?.phone,
          quoteContent: prospect?.quote_content,
          quoteStatus: prospect?.quote_status,
          reminderScheduledFor: prospect?.reminder_scheduled_for,
          professionalClientId: prospect?.professional_client_id,
          createdAt: prospect?.created_at
        },
        emailSent: !emailError,
        emailResult
      };
    } catch (error) {
      console.error('Error creating professional prospect:', error);
      throw error;
    }
  },

  /**
   * Send follow-up reminder to prospects who haven't responded
   * @param {string} prospectId - Prospect ID
   * @returns {Promise<Object>} Email sending status
   */
  async sendFollowUpReminder(prospectId) {
    try {
      // Get prospect details
      const { data: prospect, error: fetchError } = await supabase
        ?.from('prospects')
        ?.select('*')
        ?.eq('id', prospectId)
        ?.single();

      if (fetchError) throw fetchError;

      // Calculate days since quote
      const daysSinceQuote = Math.floor(
        (new Date() - new Date(prospect?.created_at)) / (1000 * 60 * 60 * 24)
      );

      // Extract company name from quote_items if available
      const companyName = prospect?.quote_items?.[0]?.companyName || null;

      // Send reminder email via edge function
      const { data: emailResult, error: emailError } = await supabase?.functions?.invoke(
        'send-quote-follow-up-reminder',
        {
          body: {
            prospectId: prospect?.id,
            companyName,
            contactName: prospect?.name,
            email: prospect?.email,
            quoteContent: prospect?.quote_content,
            daysSinceQuote
          }
        }
      );

      // Update reminder email timestamp if sent successfully
      if (!emailError) {
        await supabase
          ?.from('prospects')
          ?.update({ reminder_email_sent_at: new Date()?.toISOString() })
          ?.eq('id', prospectId);
      }

      return {
        success: !emailError,
        emailSent: !emailError,
        emailResult,
        error: emailError
      };
    } catch (error) {
      console.error('Error sending follow-up reminder:', error);
      throw error;
    }
  },

  /**
   * Get prospects that need follow-up reminders
   * @returns {Promise<Array>} List of prospects needing reminders
   */
  async getProspectsNeedingReminders() {
    try {
      const now = new Date()?.toISOString();
      
      const { data, error } = await supabase
        ?.from('prospects')
        ?.select('*')
        ?.lte('reminder_scheduled_for', now)
        ?.is('reminder_email_sent_at', null)
        ?.eq('quote_status', 'pending')
        ?.order('reminder_scheduled_for', { ascending: true });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching prospects needing reminders:', error);
      throw error;
    }
  },

  /**
   * Update prospect status
   * @param {string} prospectId - Prospect ID
   * @param {string} newStatus - New status (pending, confirmed, expired)
   * @returns {Promise<Object>} Update result
   */
  async updateProspectStatus(prospectId, newStatus) {
    try {
      const { data, error } = await supabase
        ?.from('prospects')
        ?.update({ quote_status: newStatus })
        ?.eq('id', prospectId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        success: true,
        prospect: data
      };
    } catch (error) {
      console.error('Error updating prospect status:', error);
      throw error;
    }
  },

  /**
   * Get all prospects (admin only)
   * @returns {Promise<Array>} List of prospects
   */
  async getAllProspects() {
    try {
      const { data, error } = await supabase
        ?.from('prospects')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(prospect => ({
        id: prospect?.id,
        name: prospect?.name,
        email: prospect?.email,
        phone: prospect?.phone,
        quoteItems: prospect?.quote_items,
        totalAmount: parseFloat(prospect?.total_amount),
        quoteContent: prospect?.quote_content,
        quoteStatus: prospect?.quote_status,
        confirmationEmailSentAt: prospect?.confirmation_email_sent_at,
        reminderEmailSentAt: prospect?.reminder_email_sent_at,
        reminderScheduledFor: prospect?.reminder_scheduled_for,
        createdAt: prospect?.created_at,
        updatedAt: prospect?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching prospects:', error);
      throw error;
    }
  }
};